import React, { useMemo } from 'react';
import { Image, StyleSheet, Text, View } from 'react-native';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';

import { EmptySlotPng } from '../../../assets/achievements';
import { useSvgId } from '../../../utils/hooks/useSvgId';
import { Pressable } from '../common/Touchable';
import AchievementIndicator from './AchievementIndicator';
import { ACHIEVEMENTS_FONT, achievementsColors, CARD_FILL, RADIUS_MD } from './achievementsTheme';

/**
 * Figma "Profile tile" (4486:11509) - one of the five slots on the "Selected for Profile" shelf.
 * The file's note: "Achievement shown on the profile. Removable adds the ✕ control; Empty is an
 * unfilled slot." A filled slot is the small "Achievement / First Win" card (3774:13100) with the
 * remove control hanging off its top-right corner at -4/-7, so the tile itself cannot clip.
 *
 * The tile opens the achievement's details; only the ✕ takes it off the profile.
 */
export const TILE_WIDTH = 54;
export const TILE_HEIGHT = 90;
const BADGE_SIZE = 46;
const PADDING_TOP = 6;
const GAP = 4;
const LABEL_HEIGHT = 28;
const EMPTY_SLOT = { x: 6, y: 8, box: 40, width: 34.641, height: 40 };
const REMOVE_RIGHT = -4;
const REMOVE_TOP = -7;

export type ProfileTileItem = {
	label: string;
	renderArtwork: (size: number) => React.ReactNode;
};

type Props = {
	item?: ProfileTileItem;
	/** The tile itself - opens the achievement. */
	onPress?: () => void;
	/** The ✕ - takes it off the profile. */
	onRemove?: () => void;
	/** The profile modal's own row draws the same tiles without the remove control. */
	removable?: boolean;
	removeLabel?: string;
	scale: number;
};

export const ProfileTile = ({
	item,
	onPress,
	onRemove,
	removable = true,
	removeLabel,
	scale,
}: Props) => {
	const styles = useMemo(() => createStyles(scale), [scale]);
	const fillId = useSvgId('achievementProfileTile');

	if (!item) {
		return (
			<View style={[styles.tile, styles.empty]}>
				<View style={styles.emptySlot}>
					<Image source={EmptySlotPng} style={styles.emptySlotArt} resizeMode='contain' />
				</View>
			</View>
		);
	}

	const content = (
		<>
			<View pointerEvents='none' style={styles.plate}>
				<Svg width='100%' height='100%'>
					<Defs>
						<LinearGradient id={fillId} x1='0' y1='0' x2='0' y2='1'>
							{CARD_FILL.map(([offset, color]) => (
								<Stop key={offset} offset={offset} stopColor={color} />
							))}
						</LinearGradient>
					</Defs>
					<Rect x='0' y='0' width='100%' height='100%' fill={`url(#${fillId})`} />
				</Svg>
			</View>

			<View style={styles.badge}>{item.renderArtwork(BADGE_SIZE * scale)}</View>
			<Text numberOfLines={1} style={styles.label}>
				{item.label}
			</Text>

			{removable && (
				<Pressable
					accessibilityRole='button'
					accessibilityLabel={removeLabel ?? item.label}
					onPress={onRemove}
					hitSlop={8}
					style={styles.remove}
				>
					<AchievementIndicator scale={scale} state='remove' />
				</Pressable>
			)}
		</>
	);

	if (!onPress) {
		return (
			<View accessible accessibilityLabel={item.label} style={styles.tile}>
				{content}
			</View>
		);
	}

	return (
		<Pressable
			accessibilityRole='button'
			accessibilityLabel={item.label}
			onPress={onPress}
			style={styles.tile}
		>
			{content}
		</Pressable>
	);
};

const createStyles = (scale: number) =>
	StyleSheet.create({
		tile: {
			width: TILE_WIDTH * scale,
			height: TILE_HEIGHT * scale,
			alignItems: 'center',
			paddingTop: PADDING_TOP * scale,
			gap: GAP * scale,
			borderRadius: RADIUS_MD * scale,
			borderWidth: Math.max(1, 1 * scale),
			borderColor: achievementsColors.panelBorder,
		},
		// The gradient is clipped on its own so the remove control can hang outside the tile.
		plate: {
			...StyleSheet.absoluteFillObject,
			borderRadius: RADIUS_MD * scale,
			overflow: 'hidden',
		},
		empty: {
			backgroundColor: achievementsColors.emptySlot,
			borderColor: achievementsColors.border,
			borderStyle: 'dashed',
		},
		emptySlot: {
			position: 'absolute',
			left: EMPTY_SLOT.x * scale,
			top: EMPTY_SLOT.y * scale,
			width: EMPTY_SLOT.box * scale,
			height: EMPTY_SLOT.box * scale,
			alignItems: 'center',
			justifyContent: 'center',
		},
		emptySlotArt: {
			width: EMPTY_SLOT.width * scale,
			height: EMPTY_SLOT.height * scale,
		},
		badge: {
			width: BADGE_SIZE * scale,
			height: BADGE_SIZE * scale,
		},
		// Sofia Sans Medium 10, line-height 1.4, centred in a 28-tall box.
		label: {
			width: (TILE_WIDTH - 4) * scale,
			height: LABEL_HEIGHT * scale,
			color: achievementsColors.gold50,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 10 * scale,
			fontWeight: '500',
			lineHeight: 10 * 1.4 * scale,
			textAlign: 'center',
		},
		remove: {
			position: 'absolute',
			right: REMOVE_RIGHT * scale,
			top: REMOVE_TOP * scale,
		},
	});

export default ProfileTile;
